import { mapState, mapMutations, mapActions, mapGetters } from 'vuex';
import { debounce } from 'lodash-es';

import SpacesEditorBody from '@sections/SpacesEditor/SpacesEditorBody';

import InlineCommentMixin from '@/utils/mixins/inlineCommentMixin';

import asyncTryCatchHelper from '@/utils/helpers/asyncTryCatchHelper';
import { DATA_PROMISE_KEYS } from '@/utils/constants/dataPromise';
import { logError } from '@/utils/helpers/logger.utility';
import {
  getStampedContentRevision,
  stampContentRevision,
  isDraftOutdated as checkDraftOutdated,
} from '@/utils/editor/otDraftStaleness';

// @vue/component
export default {
  name: 'SpacesEditorCollaborativeBody',
  extends: SpacesEditorBody,
  mixins: [InlineCommentMixin],

  data() {
    return {
      otClientInstance: null,
      hasNeverConnectedToOtSuccessfully: true,
      otConnectionTimeout: null,
      // The published-revision stamp on the OT root that this client has already
      // accounted for. Used to detect when another editor reconciles the shared
      // draft so we can auto-clear our "new version available" banner.
      lastSeenContentRevisionStamp: null,
    };
  },

  computed: {
    ...mapState('ui/spacesEditor', {
      hasOtConnection: 'hasOtConnection',
      hasOtConnectionError: 'hasOtConnectionError',
      usersPresentList: 'usersPresentList',
      hasPendingSync: 'hasPendingSync',
      isEditorActive: 'isEditorActive',
    }),

    ...mapState('ui/inlineComments', {
      activeCommentId: 'activeCommentId',
      shouldShowCommentsGutter: 'shouldShowCommentsGutter',
    }),

    ...mapState('pages', {
      updatedPageAvailableFromSocket: 'updatedPageAvailableFromSocket',
    }),

    ...mapGetters('users', {
      users: 'list',
    }),

    ...mapGetters('navigation', ['activePage', 'activeSpaceId', 'activePageId']),

    ...mapGetters('ui/spacePage', ['shouldResyncOtContent', 'shouldResetOtDraftToLatest', 'isDraftOutdated']),

    // The comment anchor can only be resolved once the inline comment list has
    // been fetched; before that `activeInlineComments` is empty and the anchor
    // would be reported as deleted or resolved.
    canScrollToCommentAnchor() {
      return this.isTinyMCEReady
        && this.$store.getters['loading/hasDataLoaded'](DATA_PROMISE_KEYS.INLINE_COMMENTS);
    },

    richTextOtEventListeners() {
      return {
        'ot-operation': this.onOtOperation,
        'ot-presence': this.onOtPresence,
        'ot-nothing-pending': this.onOtNothingPending,
        'ot-when-nothing-pending': this.onOtWhenNothingPending,
        'ot-load': this.onOtLoad,
        'ot-local-operation': this.onLocalOtOperation,
        'ot-connected': this.onOtConnected,
        'ot-connecting': this.onOtConnecting,
        'ot-disconnected': this.onOtDisconnected,
        'ot-connection-error': this.onOtConnectionError,
      };
    },

    richTextOtProps() {
      return {
        'ot-document-id': this.otConfig.documentId,
        'ot-collection-name': this.otConfig.collectionName,
      };
    },
  },

  methods: {
    ...mapMutations('ui/spacesEditor', [
      'setHasPendingSync',
      'setHasOtConnection',
      'setHasOtConnectionError',
      'setHasOtConnectionFailed',
      'setLastSyncedContent',
      'setShouldShowEstablishingConnectionScreen',
      'setUsersPresentList',
      'clearUsersPresentList',
    ]),

    ...mapMutations('ui/inlineComments', [
      'setActiveCommentId',
    ]),

    ...mapActions('ui/inlineComments', ['resetStoredScrollPositions']),

    ...mapMutations('inlineComments', ['clearCommentsWithNewReplies']),

    ...mapActions('alerts', ['showAlert']),

    ...mapActions('socket', ['releaseSocketDataForModule']),

    ...mapActions('users', { fetchUsers: 'fetchList' }),

    ...mapActions('ui/spacePage', ['setShouldResyncOtContent', 'setShouldResetOtDraftToLatest', 'setIsDraftOutdated']),

    async onOtPresence({ src, presence }) {
      if (!presence) {
        // remove user when they leave
        const removedPresence = this.usersPresentList.filter((u) => u.src !== src);
        this.setUsersPresentList(removedPresence);
        return;
      }
      // get the users if not present already
      if (!this.users || this.users.length === 0) {
        const [err] = await asyncTryCatchHelper(this.fetchUsers);

        if (err) {
          this.showAlert({ type: 'error', message: 'Unable to retrieve the user list' });
          return;
        }
      }

      // set the user that is present in this session
      const user = this.users.find((u) => u.id === Number(presence.userId));

      if (user && presence) {
        const presenceColour = presence.backgroundColor;

        /* eslint-disable-next-line no-param-reassign */
        presence.label = this.$options.filters.fullName(user);

        /* eslint-disable-next-line no-param-reassign */
        presence.avatar = user.avatar;

        /* eslint-disable-next-line no-underscore-dangle, no-param-reassign */
        presence.labelNode.style.cssText += 'font-size:14px;font-weight:semi-bold';

        /* eslint-disable-next-line no-underscore-dangle, no-param-reassign */
        presence.barNode.setAttribute('data-user-id', presence.userId);

        this.setUsersPresentList([{ src, presenceColour, user }, ...this.usersPresentList]);
      }
    },

    /* eslint-disable-next-line func-names */
    onOtNothingPending: debounce(function () {
      this.setHasPendingSync(false);
      this.setLastSyncedContent(this.content);
    }, 100),

    onOtWhenNothingPending({ editor }) {
      this.onEditorReady();
      /* eslint-disable-next-line no-param-reassign */
      this.otClientInstance = editor.plugins.ot.client;

      this.clearOtConnectionTimeout();

      this.startImgMutationObserver(editor);

      if (!this.isEditorActive) {
        this.disconnectOt();
      }
    },

    onOtLoad({ editor }) {
      this.content = editor.getContent();
      this.initCommentEventListeners();
      this.bootstrapOrDetectDraftStaleness(editor);
    },

    onOtOperation({ source }) {
      // Operation was submitted by a remote user
      if (!source) {
        this.refreshAnchorPopover();
        this.repositionAnchorPopover();
        this.clearBannerIfDraftReconciledRemotely();
      }
    },

    /* eslint-disable-next-line func-names */
    onLocalOtOperation: debounce(function () {
      this.setHasPendingSync(true);
    }, 100),

    onOtConnecting() {
      this.clearOtConnectionTimeout();
      this.startOtConnectionTimeout();
    },

    onOtConnected() {
      this.setHasOtConnection(true);
      this.hasNeverConnectedToOtSuccessfully = false;
      this.setHasOtConnectionError(false);
      this.setShouldShowEstablishingConnectionScreen(false);
      this.clearOtConnectionTimeout();
    },

    onOtDisconnected() {
      this.setHasOtConnection(false);
      this.setHasOtConnectionError(false);
      this.clearOtConnectionTimeout();

      // dont start timeout if user has just closed the editor
      if (this.isEditorActive) {
        this.startOtConnectionTimeout();
      }
    },

    onOtConnectionError({ error, editor }) {
      const otClient = editor?.plugins?.ot?.client;
      if (!otClient) {
        return;
      }

      if (!this.userCanEditPage) {
        otClient.shouldConnect = false;
        return;
      }

      // ot-client retries every failure. When launchpad refused the ticket or
      // API key (see markIfPermanent in the apiKeys store), retrying cannot
      // succeed, so stop and say so instead of waiting forever.
      if (error?.isPermanent) {
        otClient.shouldConnect = false;
        this.clearOtConnectionTimeout();
        this.setHasOtConnectionFailed(true);
      }
    },

    tryConnectOt() {
      if (this.otClientInstance) {
        this.otClientInstance.shouldConnect = true;
      }
    },

    disconnectOt() {
      if (this.otClientInstance && this.otClientInstance.shouldConnect) {
        this.otClientInstance.shouldConnect = false;
      }
    },

    onOtConnectionTimeout() {
      if (!this.isEditorActive) {
        return;
      }

      if (!this.hasOtConnection) {
        this.setHasOtConnectionError(true);
      }

      if (this.hasNeverConnectedToOtSuccessfully) {
        this.setShouldShowEstablishingConnectionScreen(true);
      }
    },

    startOtConnectionTimeout() {
      this.otConnectionTimeout = setTimeout(this.onOtConnectionTimeout, 3000);
    },

    clearOtConnectionTimeout() {
      this.otConnectionTimeout = clearTimeout(this.otConnectionTimeout);
    },

    onCloseTab(e) {
      if (this.hasPendingSync) {
        /* eslint-disable no-param-reassign */
        e.returnValue = 'Unsynced changes will be lost. Are you sure you want to close this tab?';
      }
    },

    // On load, reconcile the shared OT draft against the latest published
    // content revision. The draft can lag behind when content is published
    // outside the editor (e.g. via the API), because those publishes never
    // touch the OT document.
    async bootstrapOrDetectDraftStaleness(editor) {
      const otClient = (editor && editor.plugins && editor.plugins.ot && editor.plugins.ot.client)
        || this.otClientInstance;

      // Stamping is an OT write, so it needs edit access, a client and a page.
      if (!otClient || !this.userCanEditPage || !this.activePage) {
        return;
      }

      // A newer published version may already be pending — e.g. it landed while
      // the user was in read mode, and read->edit navigation skips the page
      // refetch (see fetchData in router/routes.js). In that case activePage is
      // stale, so refetch before stamping/comparing, otherwise we'd stamp a
      // stale revision as "in sync" and mask the very staleness we detect.
      if (this.updatedPageAvailableFromSocket) {
        const [fetchErr] = await asyncTryCatchHelper(() => this.$store.dispatch('pages/fetchSingle', {
          customUrlFnArgs: [this.activeSpaceId],
          id: this.activePageId,
        }, { root: true }));

        // Bail if the fetch failed or the editor was closed while fetching.
        if (fetchErr || !this.isEditorActive || !this.activePage) {
          return;
        }
      }

      const { contentRevision } = this.activePage;

      if (contentRevision === undefined || contentRevision === null) {
        return;
      }

      const stampedRevision = getStampedContentRevision(otClient);

      // Never-stamped draft: record the current revision WITHOUT touching the
      // content, so any unpublished draft edits are preserved. Staleness
      // becomes detectable from the next publish onward.
      if (stampedRevision === null || stampedRevision === '') {
        stampContentRevision(otClient, contentRevision);
        this.lastSeenContentRevisionStamp = String(contentRevision);
        return;
      }

      this.lastSeenContentRevisionStamp = stampedRevision;
      this.evaluateDraftStaleness(otClient, stampedRevision, contentRevision);
    },

    // Decides whether to surface the "draft outdated" banner. A stamp that lags
    // the published revision is not enough on its own — right after publishing
    // via the UI the draft already equals the published content and only the
    // stamp is behind. So we only show the banner when the content genuinely
    // differs; otherwise we just bring the stamp up to date silently.
    //
    // IMPORTANT: the caller must ensure activePage holds the LATEST published
    // content before calling this (fresh on page load; explicitly refetched in
    // the live socket path), otherwise the content comparison is meaningless.
    evaluateDraftStaleness(otClient, stampedRevision, contentRevision) {
      if (!checkDraftOutdated(stampedRevision, contentRevision)) {
        return;
      }

      if (this.isDraftContentInSyncWithPublished(otClient)) {
        stampContentRevision(otClient, contentRevision);
        this.lastSeenContentRevisionStamp = String(contentRevision);
        return;
      }

      this.setIsDraftOutdated(true);
    },

    // Whether the live draft content matches the latest published content. Used
    // to tell a genuinely stale draft apart from one whose version stamp merely
    // lags the published revision (e.g. straight after a UI publish). The root
    // node is ignored so the version stamp itself is not counted as a difference.
    isDraftContentInSyncWithPublished(otClient) {
      const publishedContent = this.activePage && this.activePage.content;

      if (typeof publishedContent !== 'string' || !otClient.otModel) {
        return false;
      }

      try {
        const publishedOtModel = otClient.contentToOtModel(publishedContent);

        return otClient.compareOtModels(otClient.otModel, publishedOtModel, {
          ignoreRootNode: true,
          ignoreNodeIds: true,
        });
      } catch (err) {
        // If the comparison fails we fall back to treating the draft as
        // outdated (so we err towards prompting rather than silently masking a
        // real change) — but log it, otherwise a comparison regression looks
        // identical to genuine staleness and surfaces as a spurious banner with
        // no way to diagnose the real cause.
        logError('otDraftStaleness: failed to compare draft against published content', err);
        return false;
      }
    },

    // When another editor reconciles the shared draft, its OT operations —
    // including the updated published-revision stamp on the root node — stream
    // into our editor. If our banner is showing and that stamp has advanced, the
    // draft is now up to date for us too, so bring our page state in line and
    // drop the banner instead of leaving a stale prompt (or requiring a
    // redundant, idempotent re-reset).
    clearBannerIfDraftReconciledRemotely() {
      const stampedRevision = getStampedContentRevision(this.otClientInstance);

      if (stampedRevision === null || stampedRevision === '') {
        return;
      }

      // Nothing changed since we last accounted for the stamp — a plain
      // collaborative edit, not a reconcile.
      if (stampedRevision === this.lastSeenContentRevisionStamp) {
        return;
      }

      this.lastSeenContentRevisionStamp = stampedRevision;

      // The stamp advanced via a remote reconcile. If our draft-outdated banner
      // is up, the draft is now current for us too, so bring page state in line
      // and drop the banner (releaseSocketDataForModule is a no-op when nothing
      // is buffered; otherwise it applies the pending page update so
      // activePage.contentRevision catches up to the reconciled draft).
      if (this.isDraftOutdated) {
        this.releaseSocketDataForModule({ storeModule: 'pages' });
        this.setIsDraftOutdated(false);
      }
    },

    // Replaces the shared OT draft with the latest published content. Triggered
    // (via the shouldResetOtDraftToLatest flag) when the user confirms the
    // "new version available" banner while editing.
    //
    // This is a single-document, socket-based operation: setContent generates OT
    // operations that propagate to every connected editor, so reconciling once
    // reloads the content for everyone. There is intentionally no per-user gate
    // (e.g. presence guard) — staleness can only be resolved for the shared draft
    // as a whole, not per user.
    async replaceOtDraftWithPublishedContent() {
      const otClient = this.otClientInstance;
      const { tinymceInstance } = this;

      if (!otClient || !tinymceInstance || !this.userCanEditPage) {
        return;
      }

      // Make sure we hold the latest published content before overwriting.
      const [fetchErr] = await asyncTryCatchHelper(() => this.$store.dispatch('pages/fetchSingle', {
        customUrlFnArgs: [this.activeSpaceId],
        id: this.activePageId,
      }, { root: true }));

      if (fetchErr) {
        this.showAlert({ type: 'error', message: 'Unable to load the latest version of this page.' });
        return;
      }

      // The editor may have been closed while we were fetching — don't write
      // content to (or stamp) a torn-down editor.
      if (!this.isEditorActive || !this.otClientInstance || !this.tinymceInstance) {
        return;
      }

      const publishedContent = this.activePage && this.activePage.content;

      if (typeof publishedContent !== 'string') {
        return;
      }

      // setContent generates OT operations that update the shared draft for
      // every client; re-stamp once they have synced so the draft is no longer
      // flagged as stale.
      tinymceInstance.setContent(publishedContent);

      otClient.whenNothingPending(() => {
        const reconciledRevision = this.activePage && this.activePage.contentRevision;
        stampContentRevision(otClient, reconciledRevision);
        if (reconciledRevision !== undefined && reconciledRevision !== null) {
          this.lastSeenContentRevisionStamp = String(reconciledRevision);
        }
      });

      this.setIsDraftOutdated(false);
    },
  },

  beforeDestroy() {
    this.clearOtConnectionTimeout();
    this.setHasOtConnectionFailed(false);
    this.clearUsersPresentList();
    this.clearCommentsWithNewReplies();

    // Clear the editor-only "draft outdated" flag so it never leaks into a later
    // session or the read view.
    this.setIsDraftOutdated(false);

    if (this.activeCommentId) {
      this.setActiveCommentId(null);
      this.resetStoredScrollPositions();
    }
  },

  watch: {
    // A newer published version arriving via socket while editing may mean the
    // shared draft is now based on outdated content. We refetch the page first so
    // the comparison runs against the latest published content (the socket update
    // is buffered and hasn't been applied to activePage yet), then re-evaluate.
    // This also prevents a false positive when the update was our own UI publish
    // (draft already in sync). The read-mode banner is driven separately by this
    // same socket flag; we don't touch it here.
    async updatedPageAvailableFromSocket(isAvailable) {
      if (!isAvailable || !this.isEditorActive || !this.userCanEditPage) {
        return;
      }

      const otClient = this.otClientInstance;

      if (!otClient) {
        return;
      }

      const [fetchErr] = await asyncTryCatchHelper(() => this.$store.dispatch('pages/fetchSingle', {
        customUrlFnArgs: [this.activeSpaceId],
        id: this.activePageId,
      }, { root: true }));

      if (fetchErr) {
        return;
      }

      // The editor may have been closed while we were fetching. Don't set the
      // editor-only banner state on a torn-down editor — it would persist in the
      // store and leak into the next session as a stale banner.
      if (!this.isEditorActive) {
        return;
      }

      const stampedRevision = getStampedContentRevision(otClient);
      const contentRevision = this.activePage && this.activePage.contentRevision;

      if (
        stampedRevision === null
        || stampedRevision === ''
        || contentRevision === undefined
        || contentRevision === null
      ) {
        return;
      }

      this.evaluateDraftStaleness(otClient, stampedRevision, contentRevision);
    },

    canScrollToCommentAnchor: {
      immediate: true,
      handler(canScroll) {
        if (canScroll) {
          this.scrollToCommentAnchor();
        }
      },
    },
    isEditorActive: {
      immediate: true,
      handler(isEditing) {
        if (isEditing) {
          this.tryConnectOt();

          window.addEventListener('beforeunload', this.onCloseTab);
        } else {
          this.disconnectOt();
          window.removeEventListener('beforeunload', this.onCloseTab);
        }
      },
    },

    hasOtConnection() {
      if (this.hasOtConnection) {
        setTimeout(() => {
          this.removeOrphanedInlineComments();
        }, 0);
      }
    },

    hasOtConnectionError(newVal) {
      // If they can't publish the page they will be disconnected from OT,
      // so don't show them the alert.
      if (newVal) {
        this.showAlert({
          type: 'error',
          message: 'Draft auto save and collaborative editing connection lost',
        });
      } else {
        this.showAlert({
          type: 'success',
          message: 'Draft auto save and collaborative editing connection back online',
        });
      }
    },

    // Used to resync ot content to retrieve latest inline comments markers made in editor
    shouldResyncOtContent: {
      immediate: false,
      handler(shouldResyncOtContent) {
        if (shouldResyncOtContent) {
          this.tryConnectOt();

          setTimeout(() => {
            this.disconnectOt();
            this.setShouldResyncOtContent(false);
          }, 2000);
        }
      },
    },

    // Set when the user confirms the "new version available" banner while
    // editing; replaces the stale shared OT draft with the latest published
    // content.
    shouldResetOtDraftToLatest: {
      immediate: false,
      handler(shouldReset) {
        if (!shouldReset) {
          return;
        }

        this.replaceOtDraftWithPublishedContent().finally(() => {
          this.setShouldResetOtDraftToLatest(false);
        });
      },
    },
  },
};
